import React, { useContext, useState } from 'react';
import { ACTION_LINK_HEIGHT } from '../../../../../Footer/ActionLink';
import { SubmissionContext } from '../../Context/SubmissionProvider';
import { Box, Tab, Tabs } from '@mui/material';
import SubmissionOverviewHeader from './SubmissionOverviewHeader';
import SubmissionTabVersions from './SubmissionTabVersions/SubmissionTabVersions';
import SubmissionTabMetrics from './SubmissionTabMetrics/SubmissionTabMetrics';
import SubmissionTabInfos from './SubmissionTabInfos/SubmissionTabInfos';
import SubmissionTabLeaderboard from './SubmissionTabLeaderboard/SubmissionTabLeaderboard';
import { isDefined } from '@crazygames/utils/isDefined';
import { submissionHadBasicLaunch } from '../../../../../../helpers/flowHelpers';
import { NO_METRICS_STATUSES } from '../../../../../../helpers/metricsHelpers';
import Chart from '../../../../../../../common/icons/Chart';
import { submissionOverviewTabpanelSx, submissionOverviewTabsSx, submissionOverviewTabSx } from './submissionOverview.styles';
import Database from '../../../../../../../common/icons/Database';
import Leaderboard from '../../../../../../../common/icons/Leaderboard';
import Info from '../../../../../../../common/icons/Info';

type SubmissionOverviewProps = {
  goToInfoUpdate(): void;
  createNewGameBuild(): void;
  goToOverview(): void;
};

const SubmissionOverview: React.FC<SubmissionOverviewProps> = ({ goToInfoUpdate, createNewGameBuild, goToOverview }) => {
  const { submission, game } = useContext(SubmissionContext);
  const [selectedTab, setSelectedTab] = useState(0);

  if (!submission) {
    return null;
  }

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const hasMetrics = !NO_METRICS_STATUSES.includes(submission.status) || submissionHadBasicLaunch(submission.statusChanges);
  const hasLeaderboard = isDefined(submission.game?.rawLeaderboardOptions) && submission.game.rawLeaderboardOptions !== 'null';

  const SUBMISSION_TABS = [
    {
      id: 'metrics',
      isVisible: hasMetrics,
      icon: <Chart />,
      label: 'Metrics',
      content: () => <SubmissionTabMetrics submission={submission} />,
    },
    {
      id: 'versions',
      isVisible: true,
      icon: <Database />,
      label: 'Game Versions',
      content: () => <SubmissionTabVersions createNewGameBuild={createNewGameBuild} />,
    },
    {
      id: 'info',
      isVisible: true,
      icon: <Info />,
      label: 'Game Info',
      content: () => <SubmissionTabInfos goToOverview={goToOverview} />,
    },
    {
      id: 'leaderboard',
      isVisible: hasLeaderboard,
      icon: <Leaderboard />,
      label: 'Leaderboard',
      content: () => <SubmissionTabLeaderboard />,
    },
  ];

  const visibleTabs = SUBMISSION_TABS.filter((tab) => tab.isVisible);

  return (
    <Box sx={{ paddingBottom: `${ACTION_LINK_HEIGHT}px` }}>
      <SubmissionOverviewHeader
        submission={submission}
        game={game}
        goToInfoUpdate={goToInfoUpdate}
        createNewGameBuild={createNewGameBuild}
      />

      <Tabs value={selectedTab} onChange={handleChange} sx={submissionOverviewTabsSx}>
        {visibleTabs.map((tab, i) => {
          const label = (
            <>
              {tab.icon}
              {tab.label}
            </>
          );
          return <Tab key={tab.id} value={i} label={label} sx={submissionOverviewTabSx} />;
        })}
      </Tabs>

      {visibleTabs.map((tab, i) => {
        if (i !== selectedTab) {
          return null;
        }
        return (
          <Box key={tab.id} role="tabpanel" sx={submissionOverviewTabpanelSx}>
            {tab.content()}
          </Box>
        );
      })}

      {/* @TODO (copied from previous code): Enable UserFeedback below whenever it gets implemented */}
      {/* {submission.feedback && (
        <AccordionSection title="User feedback" expanded={false}>
          <UserFeedback submission={submission} />
        </AccordionSection>
      )} */}
    </Box>
  );
};

export default SubmissionOverview;
